import {
  AnchorHTMLAttributes,
  HtmlHTMLAttributes,
  PropsWithChildren,
  ReactNode,
} from "react";
import { clsx } from "clsx";
import { Tooltip as RadixTooltip } from "radix-ui";
import { twMerge } from "tailwind-merge";

export type TooltipProps = RadixTooltip.TooltipProps & {
  title?: ReactNode;
  content?: ReactNode;
  ctaSection?: ReactNode;
  contentProps?: Partial<RadixTooltip.TooltipContentProps>;
  triggerProps?: Partial<RadixTooltip.TooltipTriggerProps>;
};

export function Tooltip({
  children,
  content,
  title,
  ctaSection,
  contentProps,
  triggerProps,
  ...rest
}: TooltipProps) {
  const { className: contentClassName, ...contentPropsRest } =
    contentProps || {};

  if (!children) return null;
  // Defensively don't display an empty tooltip.
  if (!title && !content && !ctaSection) return <>{children}</>;

  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root {...rest} delayDuration={300}>
        <RadixTooltip.Trigger {...triggerProps} className="cursor-pointer">
          {children}
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side="top"
            sideOffset={8}
            className={twMerge(
              clsx(
                "bg-surface-inverse data-[state=delayed-open]:animate-fadeIn data-[state=closed]:animate-fadeOut z-tooltip max-w-70 rounded-md px-3 py-2 text-sm break-words text-white",
                contentClassName,
              ),
            )}
            {...contentPropsRest}
          >
            {title && <TooltipTitle>{title}</TooltipTitle>}
            {content && <TooltipContentText>{content}</TooltipContentText>}
            {ctaSection && <div className="mt-3">{ctaSection}</div>}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}

type TooltipSubComponentProps<T = unknown> = PropsWithChildren<T>;

function TooltipTitle({ children }: TooltipSubComponentProps) {
  return <div className="mb-1 font-bold">{children}</div>;
}

function TooltipContentText({ children }: TooltipSubComponentProps) {
  return <div>{children}</div>;
}

export function TooltipCta({
  children,
  ...rest
}: TooltipSubComponentProps<HtmlHTMLAttributes<HTMLButtonElement>>) {
  return (
    <button
      {...rest}
      className="border-none font-bold outline-none hover:underline"
    >
      {children}
    </button>
  );
}

export function TooltipLinkCta({
  children,
  ...rest
}: TooltipSubComponentProps<AnchorHTMLAttributes<HTMLAnchorElement>>) {
  return (
    <a
      className="cursor-pointer border-none font-bold outline-none hover:underline"
      target="_blank"
      rel="noopener noreferrer"
      {...rest}
    >
      {children}
    </a>
  );
}

type ConditionalTooltipProps = TooltipProps & {
  condition: boolean;
  children: ReactNode;
};
export function ConditionalTooltip({
  condition,
  children,
  ...rest
}: ConditionalTooltipProps) {
  if (condition) {
    return (
      <Tooltip {...rest} triggerProps={{ ...rest.triggerProps, asChild: true }}>
        <span>{children}</span>
      </Tooltip>
    );
  }

  return <>{children}</>;
}
